// Murabaha ishi holati va shartnoma holati/ID mosligini tekshiruvchi markazlashgan qoidalar.
// Faqat o'qish/ko'rsatish uchun — baza yozuvlari o'zgartirilmaydi.

import i18n from '@/i18n';

export interface ConsistencyIssue {
  code: string;
  label: string;
  detail: string;
}

export interface ConsistencyResult {
  ok: boolean;
  issues: ConsistencyIssue[];
}

export interface CaseLike {
  status?: string | null;
  payment_status?: string | null;
  contract_id?: string | null;
}

export interface ContractLike {
  status?: string | null;
  contract_number?: string | null;
}

// 4 product + client_id (8 digits, 9 allowed) + 3 sequence = 15–16 digits (DOMAIN.md)
const CONTRACT_ID_RE = /^\d{15,16}$/;

export function isValidContractId(id?: string | null): boolean {
  return !!id && CONTRACT_ID_RE.test(id.trim());
}

export function checkMurabahaConsistency(
  caseRow: CaseLike | null | undefined,
  contractRow: ContractLike | null | undefined
): ConsistencyResult {
  const issues: ConsistencyIssue[] = [];
  if (!caseRow) return { ok: true, issues };

  const caseStatus = caseRow.status || '';
  const paymentStatus = caseRow.payment_status || 'none';
  const contractStatus = contractRow?.status || null;
  const contractId = caseRow.contract_id || contractRow?.contract_number || null;

  const push = (code: string, label: string, detail: string) =>
    issues.push({ code, label, detail });

  // 1. Ish holati -> kutilgan shartnoma holati
  if (caseStatus === 'Draft') {
    if (contractStatus && contractStatus !== 'draft') {
      push('draft_has_contract', i18n.t('misc_consist.draft_has_contract_l'),
        i18n.t('misc_consist.draft_has_contract_d', { status: contractStatus }));
    }
  } else if (caseStatus === 'PendingCustomerConfirmation') {
    if (contractStatus && !['draft', 'sent'].includes(contractStatus)) {
      push('pending_contract_status', i18n.t('misc_consist.pending_contract_status_l'),
        i18n.t('misc_consist.pending_contract_status_d', { status: contractStatus }));
    }
  } else if (caseStatus === 'Approved' || caseStatus === 'Completed') {
    if (!contractRow) {
      push('approved_no_contract', i18n.t('misc_consist.approved_no_contract_l'),
        i18n.t('misc_consist.approved_no_contract_d'));
    } else if (contractStatus !== 'signed') {
      push('approved_not_signed', i18n.t('misc_consist.approved_not_signed_l'),
        i18n.t('misc_consist.approved_not_signed_d', { status: contractStatus }));
    }
    if (!contractId) {
      push('missing_contract_id', i18n.t('misc_consist.missing_contract_id_l'),
        i18n.t('misc_consist.missing_contract_id_d'));
    }
  } else if (caseStatus === 'Rejected' || caseStatus === 'Cancelled') {
    if (contractStatus && contractStatus !== 'cancelled') {
      push('cancelled_active_contract', i18n.t('misc_consist.cancelled_active_contract_l'),
        i18n.t('misc_consist.cancelled_active_contract_d', { status: contractStatus }));
    }
  }

  // 2. To'lov holati -> ish holati
  if (paymentStatus === 'paid' && caseStatus !== 'Completed') {
    push('paid_not_completed', i18n.t('misc_consist.paid_not_completed_l'),
      i18n.t('misc_consist.paid_not_completed_d'));
  }
  if (paymentStatus === 'in_progress' && !['Approved', 'Completed'].includes(caseStatus)) {
    push('progress_not_approved', i18n.t('misc_consist.progress_not_approved_l'),
      i18n.t('misc_consist.progress_not_approved_d'));
  }
  if (paymentStatus === 'cancelled' && !['Cancelled', 'Rejected'].includes(caseStatus)) {
    push('payment_cancelled_case_active', i18n.t('misc_consist.payment_cancelled_case_active_l'),
      i18n.t('misc_consist.payment_cancelled_case_active_d'));
  }

  // 3. Shartnoma ID formati (4 raqam produkt + 8–9 mijoz + 3 ketma-ketlik)
  if (contractId && !isValidContractId(contractId)) {
    push('bad_id_format', i18n.t('misc_consist.bad_id_format_l'),
      i18n.t('misc_consist.bad_id_format_d', { id: contractId }));
  }

  // 4. Shartnoma yozuvi va ish yozuvidagi ID bir xil bo'lishi kerak
  if (caseRow.contract_id && contractRow?.contract_number &&
      caseRow.contract_id !== contractRow.contract_number) {
    push('id_mismatch', i18n.t('misc_consist.id_mismatch_l'),
      i18n.t('misc_consist.id_mismatch_d', { caseId: caseRow.contract_id, contractId: contractRow.contract_number }));
  }

  return { ok: issues.length === 0, issues };
}
